import { useEffect, useState } from 'react'
import {
  Stack,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  Text,
  Alert,
  Loader,
  Center,
  Modal,
  PasswordInput,
  TextInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useSearchParams } from 'react-router'
import { IconFingerprint, IconPencil, IconTrash, IconMailFast, IconAlertCircle } from '@tabler/icons-react'
import type { PasskeyCredentialSummary } from '@paperair/shared-types'
import { authClient } from '../../api/authClient'
import { getErrorMessage } from '../../api/client'

/** Re-auth modal shared by "Add a passkey" (enroll/start) and "Delete" (remove) — both the
 * server endpoints behind them require the current password (build plan §3.2, §3.3). */
function PasswordConfirmModal({
  opened,
  title,
  description,
  confirmLabel,
  confirmColor = 'blue',
  onClose,
  onConfirm,
}: {
  opened: boolean
  title: string
  description: string
  confirmLabel: string
  confirmColor?: string
  onClose: () => void
  onConfirm: (password: string) => Promise<void>
}) {
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleClose = () => {
    setPassword('')
    setError(null)
    onClose()
  }

  const handleSubmit = async () => {
    if (!password) return
    setIsSubmitting(true)
    setError(null)
    try {
      await onConfirm(password)
      handleClose()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={title} centered>
      <Stack>
        <Text size="sm" c="dimmed">
          {description}
        </Text>
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {error}
          </Alert>
        )}
        <PasswordInput
          label="Current password"
          placeholder="Your password"
          required
          autoFocus
          disabled={isSubmitting}
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button color={confirmColor} onClick={handleSubmit} loading={isSubmitting} disabled={!password}>
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function RenameModal({
  passkey,
  onClose,
  onRenamed,
}: {
  passkey: PasskeyCredentialSummary | null
  onClose: () => void
  onRenamed: (updated: PasskeyCredentialSummary) => void
}) {
  const [name, setName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setName(passkey?.name ?? '')
    setError(null)
  }, [passkey])

  const handleSubmit = async () => {
    if (!passkey || !name.trim()) return
    setIsSubmitting(true)
    setError(null)
    try {
      const updated = await authClient.passkeys.rename(passkey.id, name.trim())
      onRenamed(updated)
      onClose()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal opened={!!passkey} onClose={onClose} title="Rename passkey" centered>
      <Stack>
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {error}
          </Alert>
        )}
        <TextInput
          label="Name"
          required
          autoFocus
          maxLength={64}
          disabled={isSubmitting}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isSubmitting} disabled={!name.trim()}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export function PasskeysSection() {
  // Three states: null = still checking, false = hidden (unsupported or disabled), true = shown.
  // Per build plan §4.1: "Hidden if GET /auth/passkeys/config says disabled or the browser
  // lacks WebAuthn support."
  const [available, setAvailable] = useState<boolean | null>(null)
  const [passkeys, setPasskeys] = useState<PasskeyCredentialSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const [addModalOpened, setAddModalOpened] = useState(false)
  const [checkEmailNotice, setCheckEmailNotice] = useState(false)
  const [renameTarget, setRenameTarget] = useState<PasskeyCredentialSummary | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PasskeyCredentialSummary | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    let cancelled = false

    async function checkAvailability() {
      if (!authClient.passkeys.isSupported()) {
        if (!cancelled) setAvailable(false)
        return
      }
      try {
        const config = await authClient.passkeys.getConfig()
        if (!cancelled) setAvailable(config.enabled)
      } catch {
        if (!cancelled) setAvailable(false)
      }
    }

    checkAvailability()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (available) {
      loadPasskeys()
    }
  }, [available])

  // The launcher's passkey setup banner links here with ?addPasskey=1 to open the add flow
  // directly. Consumed once, then stripped so a reload doesn't reopen it.
  useEffect(() => {
    if (available && searchParams.get('addPasskey') === '1') {
      setAddModalOpened(true)
      const next = new URLSearchParams(searchParams)
      next.delete('addPasskey')
      setSearchParams(next, { replace: true })
    }
  }, [available, searchParams, setSearchParams])

  async function loadPasskeys() {
    setIsLoading(true)
    try {
      const list = await authClient.passkeys.list()
      setPasskeys(list)
    } catch (err) {
      notifications.show({ title: 'Error', message: getErrorMessage(err), color: 'red' })
    } finally {
      setIsLoading(false)
    }
  }

  async function handleStartEnrollment(password: string) {
    await authClient.passkeys.startEnrollment(password)
    setCheckEmailNotice(true)
  }

  async function handleDelete(password: string) {
    if (!deleteTarget) return
    await authClient.passkeys.remove(deleteTarget.id, password)
    setPasskeys((prev) => prev.filter((p) => p.id !== deleteTarget.id))
    notifications.show({ title: 'Removed', message: `"${deleteTarget.name}" has been removed`, color: 'green' })
  }

  if (available === null) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    )
  }

  if (available === false) {
    return null
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <div>
          <Text fw={500}>Passkeys</Text>
          <Text size="sm" c="dimmed">
            Sign in without a password using Touch ID, Face ID, Windows Hello, or a security key.
          </Text>
        </div>
        <Button leftSection={<IconFingerprint size={16} />} onClick={() => setAddModalOpened(true)}>
          Add a passkey
        </Button>
      </Group>

      {checkEmailNotice && (
        <Alert
          icon={<IconMailFast size={16} />}
          title="Check your email"
          color="blue"
          withCloseButton
          onClose={() => setCheckEmailNotice(false)}
        >
          We sent a confirmation link to your email address. Open it to finish adding your passkey.
        </Alert>
      )}

      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : passkeys.length === 0 ? (
        <Text c="dimmed" ta="center" py="md" size="sm">
          No passkeys yet.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Added</Table.Th>
              <Table.Th>Last used</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {passkeys.map((passkey) => (
              <Table.Tr key={passkey.id}>
                <Table.Td>
                  <Text size="sm">{passkey.name}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={passkey.backedUp ? 'green' : 'gray'} variant="light">
                    {passkey.backedUp ? 'Synced' : 'Device-only'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {new Date(passkey.createdAt).toLocaleDateString()}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {passkey.lastUsedAt ? new Date(passkey.lastUsedAt).toLocaleDateString() : 'Never'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} justify="flex-end">
                    <ActionIcon variant="subtle" onClick={() => setRenameTarget(passkey)}>
                      <IconPencil size={16} />
                    </ActionIcon>
                    <ActionIcon variant="subtle" color="red" onClick={() => setDeleteTarget(passkey)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <PasswordConfirmModal
        opened={addModalOpened}
        title="Confirm your password"
        description="Enter your password to start adding a passkey. We'll email you a confirmation link before it's saved."
        confirmLabel="Send confirmation email"
        onClose={() => setAddModalOpened(false)}
        onConfirm={handleStartEnrollment}
      />

      <PasswordConfirmModal
        opened={!!deleteTarget}
        title="Remove passkey"
        description={deleteTarget ? `Enter your password to remove "${deleteTarget.name}". This can't be undone.` : ''}
        confirmLabel="Remove"
        confirmColor="red"
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      <RenameModal
        passkey={renameTarget}
        onClose={() => setRenameTarget(null)}
        onRenamed={(updated) => {
          setPasskeys((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
          setRenameTarget(null)
        }}
      />
    </Stack>
  )
}
